import { useState, useEffect } from "react";
import {
  Card,
  Button,
  Space,
  Tag,
  Typography,
  Input,
  Modal,
  notification,
  Alert,
} from "antd";
import {
  SafetyOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { axiosInstance } from "../../providers/dataProvider";
import { ENV } from "../../config/env";
import { MfaSetupModal } from "../../components/auth/MfaSetupModal";
import { BackupCodesModal } from "../../components/auth/BackupCodesModal";

const { Text } = Typography;

interface MfaStatus {
  isEnabled: boolean;
  enabledAt?: string;
  backupCodesRemaining: number;
}

export const MfaSecurityCard: React.FC = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<MfaStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [setupOpen, setSetupOpen] = useState(false);
  const [disableModalOpen, setDisableModalOpen] = useState(false);
  const [regenerateModalOpen, setRegenerateModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const enrollmentRequired =
    localStorage.getItem("mfa_enrollment_required") === "true";

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get("auth/mfa/status");
      setStatus(data);
    } catch {
      // MFA not available
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  // Auto-open setup modal when enrollment is required
  useEffect(() => {
    if (enrollmentRequired && status && !status.isEnabled) {
      setSetupOpen(true);
    }
  }, [enrollmentRequired, status]);

  const handleDisable = async (code: string) => {
    try {
      setActionLoading(true);
      await axiosInstance.post("auth/mfa/disable", { code });
      notification.success({ message: t("auth.mfa.disabled") });
      setDisableModalOpen(false);
      fetchStatus();
    } catch {
      notification.error({ message: t("auth.mfa.invalidCode") });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRegenerate = async (code: string) => {
    try {
      setActionLoading(true);
      const { data } = await axiosInstance.post(
        "auth/mfa/backup-codes/regenerate",
        { code },
      );
      if (data.backupCodes?.length > 0) {
        setBackupCodes(data.backupCodes);
        setShowBackupCodes(true);
        setRegenerateModalOpen(false);
        fetchStatus();
      } else {
        notification.error({ message: t("auth.mfa.invalidCode") });
      }
    } catch {
      notification.error({ message: t("auth.mfa.invalidCode") });
    } finally {
      setActionLoading(false);
    }
  };

  const handleSetupComplete = () => {
    setSetupOpen(false);
    fetchStatus();
  };

  // Hide card when MFA feature is disabled via env
  if (!ENV.MFA_ENABLED) return null;
  if (loading || status === null) return null;

  return (
    <>
      <Card
        title={
          <Space>
            <SafetyOutlined />
            {t("auth.mfa.securityTitle")}
          </Space>
        }
        style={{ marginTop: 16 }}
      >
        {enrollmentRequired && !status.isEnabled && (
          <Alert
            message={t("auth.mfa.enrollmentRequired")}
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Space
          direction="vertical"
          style={{ width: "100%" }}
          size="middle"
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Text strong>{t("auth.mfa.verifyTitle")}</Text>
            {status.isEnabled ? (
              <Tag icon={<CheckCircleOutlined />} color="success">
                {t("auth.mfa.enabled")}
              </Tag>
            ) : (
              <Tag icon={<CloseCircleOutlined />} color="default">
                {t("auth.mfa.disabled")}
              </Tag>
            )}
          </div>

          {status.isEnabled && (
            <Text type="secondary">
              {t("auth.mfa.backupCodesRemaining", {
                count: status.backupCodesRemaining,
              })}
            </Text>
          )}

          <Space wrap>
            {status.isEnabled ? (
              <>
                <Button
                  danger
                  onClick={() => setDisableModalOpen(true)}
                >
                  {t("auth.mfa.disable")}
                </Button>
                <Button onClick={() => setRegenerateModalOpen(true)}>
                  {t("auth.mfa.regenerateBackupCodes")}
                </Button>
              </>
            ) : (
              <Button type="primary" onClick={() => setSetupOpen(true)}>
                {t("auth.mfa.enable")}
              </Button>
            )}
          </Space>
        </Space>
      </Card>

      {/* Setup Modal */}
      <MfaSetupModal
        open={setupOpen}
        onClose={() => setSetupOpen(false)}
        onComplete={handleSetupComplete}
      />

      {/* Disable Confirmation Modal */}
      <OtpConfirmModal
        open={disableModalOpen}
        title={t("auth.mfa.disable")}
        description={t("auth.mfa.disableConfirm")}
        loading={actionLoading}
        onConfirm={handleDisable}
        onCancel={() => setDisableModalOpen(false)}
      />

      {/* Regenerate Confirmation Modal */}
      <OtpConfirmModal
        open={regenerateModalOpen}
        title={t("auth.mfa.regenerateBackupCodes")}
        description={t("auth.mfa.regenerateConfirm")}
        loading={actionLoading}
        onConfirm={handleRegenerate}
        onCancel={() => setRegenerateModalOpen(false)}
      />

      {/* Backup Codes Display */}
      <BackupCodesModal
        open={showBackupCodes}
        codes={backupCodes}
        onClose={() => setShowBackupCodes(false)}
      />
    </>
  );
};

/** Small reusable modal that asks for an OTP code to confirm an action */
function OtpConfirmModal({
  open,
  title,
  description,
  loading,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  description: string;
  loading: boolean;
  onConfirm: (code: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
      width={380}
    >
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <Text type="secondary">{description}</Text>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Input.OTP
            length={6}
            onChange={(value) => {
              if (value.length === 6) onConfirm(value);
            }}
            disabled={loading}
            size="large"
          />
        </div>
        <Button block onClick={onCancel}>
          {t("buttons.cancel")}
        </Button>
      </Space>
    </Modal>
  );
}
